/**
 * Sizer API — the RTL/GUC scenario endpoints on the LOS pricing router.
 *
 * All three routes below are LIVE (pricing-scenario-sizers.md). The digital-sizer
 * prototype's `/sizer/save` and `/integrations/*` routes are gone: they never
 * existed on eastview-os, and the LOS serves their equivalents —
 * `/underwriting/create_loan` for the save, `/pricing/market_pulse/{zip}` for
 * market data (which resolves HouseCanary-vs-ZHVI server-side, so the FE no
 * longer calls a market-data endpoint and a trends endpoint and a rural endpoint
 * separately).
 */
import { dsHttp } from "../lib/digitalSizerHttp";

/**
 * Is this a usable scenario id?
 *
 * Scenario ids are Mongo ObjectIds (`str(doc['_id'])`), so a real one is 24 hex
 * characters and nothing else is. The check exists because of what a template
 * literal does with an absent value: interpolating a null id into
 * `/pricing/scenarios/{id}` builds a request for a scenario named "null" —
 * which the backend hands to `ObjectId()`, and which comes back as a 500 and an
 * alert card. An id this side never resolved must not become a URL.
 */
export const SCENARIO_ID_RE = /^[0-9a-f]{24}$/i;

export const isScenarioId = (v) => SCENARIO_ID_RE.test(String(v ?? "").trim());

/** A URL param → a usable id, or null. "null"/"undefined"/garbage are ABSENT. */
export const readScenarioId = (v) =>
  isScenarioId(v) ? String(v).trim() : null;

/* Thrown, never sent. Every caller already handles a failed load or save, so a
   rejected promise here is a client-side "there is no such scenario" instead of
   a 500 logged against the backend for a value the FE never had. */
const requireScenarioId = (scenarioId, verb) => {
  if (isScenarioId(scenarioId)) return String(scenarioId).trim();
  throw new Error(`Cannot ${verb} a scenario without a valid id.`);
};

export const sizerApi = {
  // POST /pricing/scenario/rtl → the full PricingResult plus `rate` (the additive
  // RTL rate block), `requirements`, `closingCostLadder`, `classExplanation`,
  // `disclaimer`, `marketData`, `payloadWarnings`, `dataChecks` — and, since
  // 2026-08-17, `scenarioId` + `scenarioVersion`: every priced run persists to
  // `loan_processing.pricing_scenarios` automatically. Prices and screens a
  // scenario without creating a deal.
  //
  // Pass `scenarioId` back in the body to REPRICE THE SAME scenario — the run
  // appends as the next immutable version instead of starting a new object.
  // Without it every reprice orphans a fresh v1 and the drift history the
  // scenario exists to record is lost.
  price: (scenario) =>
    dsHttp.post("/pricing/scenario/rtl", scenario).then((r) => r.data),

  // POST /pricing/scenario/max_leverage → { resolved, classExplanation,
  // maxLeverage[], disclaimer, payloadWarnings }. The quick check: a handful of
  // values give the borrower class and the product's per-class max-leverage
  // grid, with NO rate offered by design. Accepts the flat body
  // ({loanProductType, loanPurpose, ficoMid, experienceTotal, citizenshipStatus})
  // or any full scenario — same normalizer and resolver as /scenario/rtl, so the
  // class math is identical.
  maxLeverage: (body) =>
    dsHttp.post("/pricing/scenario/max_leverage", body).then((r) => r.data),

  // POST /underwriting/create_loan → the created deal. "Save Loan" posts the
  // SAME scenario payload the pricing call used: the sizer request is already
  // loan-model vocabulary, so there is no mapping layer (that is the reason the
  // scenario contract is stated in deal field names). The response is the full
  // loan document, so the new id is `_id`.
  save: (scenario) =>
    dsHttp.post("/underwriting/create_loan", scenario).then((r) => r.data),

  // GET /pricing/market_pulse/{zipCode} → { zipCode, source:
  // 'housecanary'|'zhvi'|null, data, zhvi }. ONE call: the server resolves
  // HouseCanary first and falls back to ZHVI, and caches HC 30 days per zip (so
  // Refresh is billing-safe). The FE renders whatever `source` says and never
  // picks it.
  marketPulse: (zipCode) =>
    dsHttp.get(`/pricing/market_pulse/${zipCode}`).then((r) => r.data),

  /* ── Scenario pipeline (persisted priced scenarios) ──────────────────────
   * Every priced RTL/DSCR run is stored with its creator and its full version
   * history; quick checks (`max_leverage`) are not, because no rate was
   * offered. Visibility is creator-or-admin: a normal user sees only their own
   * scenarios, an admin sees everyone's and can filter by `user`.
   */

  // GET /pricing/scenarios?status=&user= → { scenarios: [...] }. Rows carry the
  // CURRENT scenario + audit meta (`scenarioType, name, status, createdBy,
  // createdByCompany, createdAt, updatedAt, currentVersion, dealId, pipeline`) —
  // fetch one for `versions[]`.
  listScenarios: (params = {}) =>
    dsHttp
      .get("/pricing/scenarios", { params })
      .then((r) => r.data?.scenarios || []),

  // GET /pricing/scenarios/{id} → the full doc including the append-only
  // `versions[]` ({version, scenario, result, pricedBy, pricedAt}).
  getScenario: (scenarioId) =>
    Promise.resolve()
      .then(() => requireScenarioId(scenarioId, "load"))
      .then((id) => dsHttp.get(`/pricing/scenarios/${id}`).then((r) => r.data)),

  // PUT /pricing/scenarios/{id} → metadata only: {name?, status?, dealId?,
  // pipeline?}. LINKING A dealId MARKS THE SCENARIO CONVERTED — from then on it
  // is the baseline the created loan's real data is compared against, which is
  // why Save Loan calls this immediately after the deal comes back. Versions
  // are never editable (they only grow via a reprice carrying `scenarioId`).
  updateScenario: (scenarioId, patch) =>
    Promise.resolve()
      .then(() => requireScenarioId(scenarioId, "update"))
      .then((id) =>
        dsHttp.put(`/pricing/scenarios/${id}`, patch).then((r) => r.data),
      ),

  /* ── Scenario exception requests ─────────────────────────────────────────
   * An exception can be raised — and APPROVED — while the deal is still a
   * scenario. The row is the same object a deal exception is, with the same
   * status vocabulary and the same approval authority, so on conversion it
   * re-parents onto the loan rather than re-gating processing. That is the
   * whole reason to file one here instead of waiting for a deal id.
   */

  // POST /pricing/scenarios/{id}/exception-requests → the updated scenario doc
  // (the new row is `exceptionRequests[0]`). Body is typically a failing
  // `guidelineExceptions[]` row: `{severity, code, exceptionDescription,
  // exceptionCategory?, riskBucket?, citation?, observedValue?, threshold?,
  // requiredDocuments?[]}`. A HARD request also materializes its
  // `requiredDocuments` as real condition objects on the scenario.
  createExceptionRequest: (scenarioId, body) =>
    Promise.resolve()
      .then(() => requireScenarioId(scenarioId, "raise an exception on"))
      .then((id) =>
        dsHttp
          .post(`/pricing/scenarios/${id}/exception-requests`, body)
          .then((r) => r.data),
      ),

  // PUT /pricing/scenarios/{id}/exception-requests/{exceptionId} → status /
  // assignment / risk bucket / compensating factors. Moving to Approved or
  // Rejected is the APPROVAL step and is restricted server-side to the
  // exception-approval authority — the FE never gates it itself.
  updateExceptionRequest: (scenarioId, exceptionId, patch) =>
    Promise.resolve()
      .then(() => requireScenarioId(scenarioId, "update an exception on"))
      .then((id) =>
        dsHttp
          .put(`/pricing/scenarios/${id}/exception-requests/${exceptionId}`, patch)
          .then((r) => r.data),
      ),
};

export default sizerApi;
